import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useCategories } from "@/context/CategoriesContext";
import { useSettings } from "@/context/SettingsContext";
import { useBranch } from "@/context/BranchContext";
import BrandHeader from "@/components/BrandHeader";
import ProductDialog from "@/components/ProductDialog";
import FeaturedRotator from "@/components/FeaturedRotator";
import BranchSelectorModal from "@/components/BranchSelectorModal";
import { fetchMenu, fetchPromotions } from "@/lib/api";
import { Search, Star, Instagram, Twitter, Facebook, Music2, Phone, Mail, Car, ChefHat } from "lucide-react";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Starting price for a card: cheapest size (+ cheapest milk if any), else base price (+ cheapest milk).
const cardPrice = (item) => {
  const sizes = Array.isArray(item.sizes) ? item.sizes : [];
  const milks = Array.isArray(item.milk_options) ? item.milk_options : [];
  const minMilk = milks.length ? Math.min(...milks.map((m) => Number(m.price) || 0)) : 0;
  const base = sizes.length ? Math.min(...sizes.map((s) => Number(s.price) || 0)) : (Number(item.price) || 0);
  return { value: round2(base + minMilk), from: sizes.length > 0 || milks.length > 0 };
};
// Default calories shown on a card = first size (+ first milk), matching the dialog's default selection.
const cardCalories = (item) => {
  const sizes = Array.isArray(item.sizes) ? item.sizes : [];
  const milks = Array.isArray(item.milk_options) ? item.milk_options : [];
  const baseCal = sizes.length ? (Number(sizes[0].calories) || 0) : (Number(item.calories) || 0);
  const milkCal = milks.length ? (Number(milks[0].calories) || 0) : 0;
  return round2(baseCal + milkCal);
};

export default function MenuPage() {
  const { lang, t, isRtl } = useLang();
  const { categories } = useCategories();
  const { settings } = useSettings();
  const { branchType, isDriveThru } = useBranch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [promos, setPromos] = useState([]);

  // Load active promotions (global, independent of branch)
  useEffect(() => {
    let cancelled = false;
    fetchPromotions(true)
      .then((data) => { if (!cancelled) setPromos(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setPromos([]); });
    return () => { cancelled = true; };
  }, []);

  // Open modal on first visit if no branch selected
  useEffect(() => {
    if (!branchType) setBranchModalOpen(true);
  }, [branchType]);

  useEffect(() => {
    if (!branchType) return; // wait for selection before loading
    let cancelled = false;
    setLoading(true);
    fetchMenu({ branch_type: branchType })
      .then((data) => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [branchType]);

  // Categories visible for the current branch
  const visibleCategories = useMemo(
    () => (isDriveThru ? categories.filter((c) => !c.kitchen_required) : categories),
    [categories, isDriveThru]
  );

  // Reset active cat if it becomes hidden due to branch switch
  useEffect(() => {
    if (activeCat !== "all" && !visibleCategories.find((c) => c.key === activeCat)) {
      setActiveCat("all");
    }
  }, [visibleCategories, activeCat]);

  const featured = useMemo(() => items.filter((i) => i.is_featured), [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (activeCat !== "all") list = list.filter((i) => i.category === activeCat);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (i) => i.name_en.toLowerCase().includes(q) || i.name_ar.includes(q)
      );
    }
    return list;
  }, [items, activeCat, search]);

  const grouped = useMemo(() => {
    if (activeCat !== "all") return [{ cat: activeCat, list: filtered }];
    // Group in category order (respecting branch visibility)
    return visibleCategories
      .map((c) => ({ cat: c.key, list: filtered.filter((i) => i.category === c.key) }))
      .filter((g) => g.list.length > 0);
  }, [filtered, activeCat, visibleCategories]);

  const catName = (key) => {
    if (key === "all") return t.all;
    const c = categories.find((x) => x.key === key);
    if (!c) return key;
    return lang === "ar" ? c.name_ar : c.name_en;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <BrandHeader />

      {/* HERO removed per request — menu now starts directly with FEATURED / categories */}

      {(featured.length > 0 || promos.length > 0) && (
        <FeaturedRotator items={featured} promos={promos} onSelect={setSelected} categoryName={catName} />
      )}

      {/* CATEGORY + SEARCH */}
      <section className="sticky top-[73px] z-30 bg-[#FDFBF7]/90 backdrop-blur-md border-y border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 no-scrollbar overflow-x-auto">
            <div className={`flex gap-2 ${isRtl ? "flex-row-reverse justify-end" : ""}`}>
              {[{ key: "all" }, ...visibleCategories].map((c) => (
                <button
                  key={c.key}
                  onClick={() => setActiveCat(c.key)}
                  data-testid={`category-tab-${c.key}`}
                  className={`whitespace-nowrap rounded-full px-5 py-2 text-xs uppercase tracking-widest transition-colors ${
                    activeCat === c.key
                      ? "bg-[#0F5F63] text-[#FDFBF7]"
                      : "border border-[#0F5F63]/20 text-[#0F5F63] hover:border-[#0F5F63]"
                  }`}
                >
                  {catName(c.key)}
                </button>
              ))}
            </div>
          </div>
          <div className="relative md:w-72">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B6D] ${isRtl ? "right-4" : "left-4"}`} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchPlaceholder}
              data-testid="search-input"
              className={`w-full rounded-full bg-[#F4F2EB] border border-transparent focus:border-[#0F5F63]/40 outline-none text-sm py-2.5 ${isRtl ? "pr-10 pl-4" : "pl-10 pr-4"}`}
            />
          </div>
          <button
            type="button"
            onClick={() => setBranchModalOpen(true)}
            data-testid="branch-switch-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] px-4 py-2 text-xs uppercase tracking-widest text-[#0F5F63] transition-colors whitespace-nowrap"
            title={t.branchChange}
          >
            {isDriveThru ? <Car className="w-3.5 h-3.5" /> : <ChefHat className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">
              {branchType ? (isDriveThru ? t.branchDriveThru : t.branchFull) : t.branchChangeLabel}
            </span>
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="pulse-soft">
                <div className="aspect-[4/5] bg-[#F4F2EB] rounded-2xl" />
                <div className="mt-4 h-4 w-2/3 bg-[#F4F2EB] rounded" />
                <div className="mt-2 h-3 w-1/3 bg-[#F4F2EB] rounded" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[#5C6B6D] py-16" data-testid="empty-state">{t.empty}</p>
        ) : (
          grouped.map(({ cat, list }) => (
            <div key={cat} className="mb-16" data-testid={`section-${cat}`}>
              <div className="flex items-baseline justify-between mb-8">
                <h2 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-3xl lg:text-4xl text-[#0F5F63] font-semibold`}>
                  {catName(cat)}
                </h2>
                <span className="text-xs uppercase tracking-widest text-[#5C6B6D]">
                  {list.length} {lang === "ar" ? "صنف" : "items"}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
                {list.map((item, idx) => (
                  <button
                    key={item.id}
                    onClick={() => setSelected(item)}
                    data-testid={`product-card-${item.id}`}
                    className="group text-start fade-up"
                    style={{ animationDelay: `${idx * 60}ms` }}
                  >
                    <div className="relative overflow-hidden rounded-2xl aspect-[4/5] bg-[#F4F2EB]">
                      <img
                        src={item.image_url}
                        alt={lang === "ar" ? item.name_ar : item.name_en}
                        className="img-cover w-full h-full"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                        {item.is_featured ? (
                          <span className="rounded-full bg-[#E27B58] text-white text-[10px] uppercase tracking-widest px-2.5 py-1 inline-flex items-center gap-1">
                            <Star className="w-3 h-3 fill-white" />
                          </span>
                        ) : <span />}
                        <span className="rounded-full bg-white/90 backdrop-blur text-[#0F5F63] text-xs font-medium px-3 py-1">
                          {cardPrice(item).from ? `${t.from} ` : ""}{cardPrice(item).value} {t.currency}
                        </span>
                      </div>
                    </div>
                    <h3 className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} mt-4 text-lg lg:text-xl text-[#1A1A1A] leading-tight`}>
                      {lang === "ar" ? item.name_ar : item.name_en}
                    </h3>
                    <p className="mt-1 text-xs uppercase tracking-widest text-[#5C6B6D]">
                      {cardCalories(item)} {t.kcal}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <footer className="border-t border-black/5 py-10 mt-8" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-8 md:grid-cols-3 items-start">
          <div>
            <p className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl text-[#0F5F63] font-semibold`}>
              {lang === "ar" ? settings.brand_name_ar : settings.brand_name_en}
            </p>
            <p className="text-xs uppercase tracking-widest text-[#5C6B6D] mt-1">
              {lang === "ar" ? settings.tagline_ar : settings.tagline_en}
            </p>
          </div>
          <div className="space-y-2 text-sm text-[#5C6B6D]">
            {settings.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 hover:text-[#0F5F63]" data-testid="footer-email">
                <Mail className="w-4 h-4" /> {settings.contact_email}
              </a>
            )}
            {settings.contact_phone && (
              <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-2 hover:text-[#0F5F63]" data-testid="footer-phone">
                <Phone className="w-4 h-4" /> {settings.contact_phone}
              </a>
            )}
            <p className="text-xs mt-2">{lang === "ar" ? settings.address_ar : settings.address_en}</p>
          </div>
          <div className={`flex gap-3 ${isRtl ? "md:justify-start" : "md:justify-end"}`}>
            {settings.social_instagram && (
              <a href={settings.social_instagram} target="_blank" rel="noreferrer" data-testid="social-instagram"
                className="w-10 h-10 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] flex items-center justify-center text-[#0F5F63]">
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {settings.social_twitter && (
              <a href={settings.social_twitter} target="_blank" rel="noreferrer" data-testid="social-twitter"
                className="w-10 h-10 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] flex items-center justify-center text-[#0F5F63]">
                <Twitter className="w-4 h-4" />
              </a>
            )}
            {settings.social_facebook && (
              <a href={settings.social_facebook} target="_blank" rel="noreferrer" data-testid="social-facebook"
                className="w-10 h-10 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] flex items-center justify-center text-[#0F5F63]">
                <Facebook className="w-4 h-4" />
              </a>
            )}
            {settings.social_tiktok && (
              <a href={settings.social_tiktok} target="_blank" rel="noreferrer" data-testid="social-tiktok"
                className="w-10 h-10 rounded-full border border-[#0F5F63]/20 hover:border-[#0F5F63] flex items-center justify-center text-[#0F5F63]">
                <Music2 className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-8 pt-6 border-t border-black/5 text-xs uppercase tracking-widest text-[#5C6B6D] flex justify-between">
          <span>{lang === "ar" ? settings.footer_note_ar : settings.footer_note_en}</span>
          <span>{lang === "ar" ? settings.address_ar : settings.address_en}</span>
        </div>
      </footer>

      <ProductDialog item={selected} onOpenChange={(open) => !open && setSelected(null)} categoryName={catName} />
      <BranchSelectorModal
        open={branchModalOpen}
        onOpenChange={setBranchModalOpen}
        forceChoice={!branchType}
      />
    </div>
  );
}
